// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Đa ngôn ngữ — ADR-0004.
//
// Catalog tải TỪ SERVER, không nhúng bản sao trong bundle. Hệ quả trực tiếp:
// thêm một ngôn ngữ vào internal/i18n/locales/ là nó tự xuất hiện ở đây, không
// phải build lại client, không phải sửa dòng nào. Đó là điều kiện để cộng đồng
// đóng góp bản dịch chỉ bằng một file JSON.

import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { setLocale as setApiLocale } from './api'

export type LocaleMeta = { tag: string; name: string; native_name: string; direction: string }

type Catalog = Record<string, string>

type I18n = {
  lang: string
  locales: LocaleMeta[]
  t: (key: string, vars?: Record<string, string | number>) => string
  setLang: (tag: string) => void
}

const Ctx = createContext<I18n | null>(null)

export function useI18n(): I18n {
  const v = useContext(Ctx)
  if (!v) throw new Error('useI18n dùng ngoài I18nProvider')
  return v
}

const STORE_KEY = 'zmeet_lang'

/** langFromPath đọc ngôn ngữ từ đoạn đầu đường dẫn: /vi/j/123 → "vi". */
export function langFromPath(): string | null {
  const first = location.pathname.split('/')[1]
  return first ? first : null
}

/**
 * writeLangToPath viết ngôn ngữ vào URL mà KHÔNG tải lại trang.
 *
 * Thanh địa chỉ phải luôn khớp thứ đang hiển thị: đó là thứ người dùng sao chép
 * và gửi đi (ADR-0004 §4.1). replaceState chứ không pushState — đổi ngôn ngữ
 * không phải điều hướng, và một nút Back đổi URL mà không đổi nội dung là hành
 * vi khó hiểu.
 */
function writeLangToPath(tag: string) {
  const parts = location.pathname.split('/')
  parts[1] = tag
  history.replaceState(null, '', parts.join('/') + location.search + location.hash)
}

/**
 * syncSeoTags làm hai việc mà ADR-0004 §4.2 nói client production BẮT BUỘC phải
 * làm, và trang /dev/ cố tình không làm vì nó không được index.
 *
 * Thiếu chúng thì công cụ tìm kiếm coi /vi/…, /en/… và /ja/… là ba trang trùng
 * nội dung, rồi tự chọn MỘT bản để hiển thị cho tất cả mọi người — kể cả người
 * đang tìm bằng tiếng Việt.
 */
function syncSeoTags(lang: string, locales: LocaleMeta[]) {
  document.documentElement.lang = lang
  const dir = locales.find((l) => l.tag === lang)?.direction
  document.documentElement.dir = dir === 'rtl' ? 'rtl' : 'ltr'

  document.head.querySelectorAll('link[data-i18n-seo]').forEach((el) => el.remove())

  const rest = location.pathname.split('/').slice(2).join('/')
  const href = (tag: string) => `${location.origin}/${tag}/${rest}${location.search}`

  const add = (rel: string, hreflang: string | null, url: string) => {
    const link = document.createElement('link')
    link.rel = rel
    if (hreflang) link.hreflang = hreflang
    link.href = url
    link.setAttribute('data-i18n-seo', '')
    document.head.appendChild(link)
  }

  add('canonical', null, href(lang))
  for (const l of locales) add('alternate', l.tag, href(l.tag))
  // x-default trỏ về bản không giả định gì về người truy cập.
  add('alternate', 'x-default', href('en'))
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState('en')
  const [catalog, setCatalog] = useState<Catalog>({})
  const [locales, setLocales] = useState<LocaleMeta[]>([])
  const [ready, setReady] = useState(false)

  // Chọn ngôn ngữ theo ĐÚNG thứ tự ưu tiên mà server dùng (ADR-0004 §4):
  // đường dẫn → lựa chọn đã lưu → trình duyệt → mặc định.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      const res = await fetch('/v1/locales')
      const { locales: list, default: fallback } = (await res.json()) as {
        locales: LocaleMeta[]
        default: string
      }
      if (cancelled) return
      setLocales(list)

      const pick = (want: string | null) => {
        if (!want) return null
        const w = want.toLowerCase()
        return (
          list.find((l) => l.tag.toLowerCase() === w)?.tag ??
          list.find((l) => l.tag.toLowerCase() === w.split('-')[0])?.tag ??
          null
        )
      }
      const fromPath = pick(langFromPath())
      const chosen =
        fromPath ?? pick(localStorage.getItem(STORE_KEY)) ?? pick(navigator.language) ?? fallback

      await load(chosen, list, chosen !== fromPath)
      if (!cancelled) setReady(true)
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  async function load(tag: string, list: LocaleMeta[], updatePath: boolean) {
    const res = await fetch(`/v1/locales/${encodeURIComponent(tag)}`)
    if (!res.ok) return
    const bundle = (await res.json()) as { tag: string; messages: Catalog }

    setCatalog(bundle.messages)
    setLangState(bundle.tag)
    setApiLocale(bundle.tag)
    localStorage.setItem(STORE_KEY, bundle.tag)
    if (updatePath) writeLangToPath(bundle.tag)
    syncSeoTags(bundle.tag, list)
    document.title = bundle.messages['ui.app.name'] ?? 'Z-Meet'
  }

  const value = useMemo<I18n>(
    () => ({
      lang,
      locales,
      setLang: (tag) => void load(tag, locales, true),
      // Khoá chưa tải thì trả về CHÍNH khoá. Xấu trên màn hình, nhưng grep được
      // và nói thẳng vấn đề — hơn hẳn một chuỗi rỗng im lặng.
      t: (key, vars) => {
        let s = catalog[key] ?? key
        if (vars) for (const [k, v] of Object.entries(vars)) s = s.replaceAll(`{${k}}`, String(v))
        return s
      },
    }),
    [lang, locales, catalog],
  )

  // Không dựng giao diện trước khi có catalog: hiện một màn hình toàn khoá thô
  // rồi thay bằng câu thật là một cú nhấp nháy khó chịu ở mỗi lần tải.
  if (!ready) return null
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}
